import { useRef, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

import { loaders } from '../utils/loaders';

import { formatCurrency } from '../utils';
import cartUtils from '../utils/cart';
import productUtils from '../utils/productUtils';
import { default as auth } from '../utils/auth';

import { submitOrder } from '../actions/checkout';
import { saveCart, removeCart } from '../actions/cart';
import { addAddress } from '../actions/address';

import Alert from 'react-s-alert';
import AddressSelector from '../components/checkout/AddressSelector.jsx';
import ProductTable from '../components/ProductTable.jsx';
import PaymentForm from '../components/checkout/PaymentForm.jsx';
import PromoEntry from '../components/checkout/PromoEntry.jsx';
import Loading from '../components/Loading.jsx';
import Input from '../components/form/Input.jsx';
import Checkbox from '../components/form/Checkbox.jsx';
import TextArea from '../components/form/TextArea.jsx';
import Totals from '../components/checkout/Totals.jsx';
import FormattedNumber from '../components/FormattedNumber.jsx';
import { useUser } from './User';

const Checkout = () => {
    const params = useParams();
    const navigate = useNavigate();
    const { user, org, sop, inProgress: userLoading } = useUser(params.user_id);
    const [cart, setCart] = useState({ _id: params.cart_id || 'none', items: [] });
    const [inProgress, setInProgress] = useState(true);
    const [inProcess, setInProcess] = useState(false);
    const [paymentType, setPaymentType] = useState('invoice');
    const [shouldRemoveCart, setShouldRemoveCart] = useState(false);
    const [addresses, setAddresses] = useState([]);

    let order_notes = useRef();
    let _shipping_selector = useRef();
    let _billing_selector = useRef();
    let _po_number = useRef();
    let _shipping_attn = useRef();
    let payment_form = useRef();

    const refreshCart = useCallback(async () => {
        let resp = await axios.request({
            url: `/customers/${params.user_id}/carts/${params.cart_id}`,
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Session-Key': auth.getSID()
            }
        });
        setInProgress(false);
        setCart(resp.data);
    }, [params.cart_id, params.user_id]);

    useEffect(() => {
        (async () => {
            const _addresses = await loaders.user_addresses.load(params.user_id);
            setAddresses(_addresses);
            refreshCart();
        })();
    }, [refreshCart, params.user_id]);

    const handlePaymentSelect = (event) => {
        setPaymentType(event.currentTarget.value);
    };

    const handleRemoveCheck = (checked) => {
        setShouldRemoveCart(checked);
    };

    const processPayment = async (shippingAddress, billingAddress) => {
        try {
            let paymentData = {
                type: paymentType,
                po_number: _po_number.current.getValue()
            };

            if (paymentType === 'invoice') {
                return paymentData;
            }

            if (!payment_form.current.validate()) {
                throw new Error('Form Invalid');
            }

            let payment = payment_form.current.createObject();

            paymentData = {
                ...paymentData,
                card_type: payment.type,
                first_name: user.name.given,
                last_name: user.name.family,
                last4: payment.last4,
                expire_month: payment.expire_month,
                expire_year: payment.expire_year,
                transaction_id: null
            };

            let paymentAuthData = {
                payment: {
                    card_number: payment.number,
                    expire: `${payment.expire_month}${payment.expire_year.slice(2, 4)}`,
                    cvv2: payment.cvv2,
                    amount: formatCurrency(cartUtils.getGrandTotal(cart.items, org.price_contracts, true)),
                    po_number: _po_number.current.getValue() || '',
                    currency: org.currency
                },
                customer: {
                    email: user.email,
                    _id: user._id
                },
                billing_address: billingAddress,
                shipping_address: shippingAddress
            };

            let json = await auth.validatePayment(paymentAuthData);
            if (json.RESULT[0] > 0) {
                throw new Error('There was an error authorizing your credit card.');
            }
            paymentData.transaction_id = json.PNREF[0];
            return paymentData;
        } catch (error) {
            throw error;
        }
    };

    const handleSubmit = async () => {
        let shippingAddressData = _shipping_selector.current.getValue();
        let shippingAddress = {
            ...shippingAddressData,
            to: shippingAddressData.to || org.name.long,
            attn: _shipping_attn.current.getValue()
        };

        let billingAddress = _billing_selector.current.getValue();

        setInProcess(true);

        let orderData = {
            cart_id: cart._id,
            shipping_address: cartUtils.cleanAddress(shippingAddress),
            billing_address: cartUtils.cleanAddress(billingAddress),
            customer_id: user._id,
            currency: org.currency,
            split_by_allocations: org.split_by_allocations || false,
            payment: {},
            sop_id: cart.sop_id || null,
            is_pif: cart.is_pif,
            items: cart.items.map((item) => {
                let discount = productUtils.getContractDiscount(item, org.price_contracts);
                return productUtils.getOrderStub(item, discount, org.currency);
            }),
            netsuite_entity: user.netsuite_entity,
            sub_total: formatCurrency(cartUtils.getSubtotal(cart.items)),
            discount: formatCurrency(cartUtils.getDiscount(cart.items, org.price_contracts)),
            shipping: formatCurrency(cartUtils.getShipping(cart.items, cart.is_pif, org.currency)),
            grand_total: formatCurrency(cartUtils.getGrandTotal(cart.items, org.price_contracts, true)),
            notes: order_notes.current.getValue()
        };

        try {
            let paymentData = await processPayment(billingAddress, shippingAddress);
            orderData.payment = paymentData;
            let resp = await submitOrder(orderData);
            let nextUrl = `/customer/${params.user_id}/orders/${resp.data.order_number}`;
            setInProcess(false);

            billingAddress.save &&
                addAddress(user._id, {
                    ...cartUtils.cleanAddress(billingAddress),
                    primary_shipping: false,
                    primary_billing: false
                });

            shippingAddressData.save &&
                addAddress(user._id, {
                    ...cartUtils.cleanAddress(shippingAddressData),
                    primary_shipping: false,
                    primary_billing: false
                });

            loaders.user_carts.clear(params.user_id);

            if (shouldRemoveCart) {
                await removeCart(params.user_id, params.cart_id);
                navigate(nextUrl);
                return;
            }

            //clear cart
            if (!cart.sop_id) {
                await saveCart(params.user_id, params.cart_id, { items: [] });
                navigate(nextUrl);

                return;
            }

            if (cart.sop_id) {
                navigate(nextUrl);
            }
        } catch (error) {
            setInProcess(false);
            Alert.error(error.message);
            throw error;
        }
    };

    const handleApplyPromo = (data) => {
        setCart(data);
    };

    if (inProgress || userLoading) {
        return <Loading />;
    }
    let sopSub = sop && sop.find((sub) => sub.internal_id === parseInt(cart.sop_id, 10));
    let canDelete = !cart.is_primary && !cart.is_pif && !cart.sop_id;
    let onlyProducts = cart.items.filter((i) => i.format && !i.format.includes('warranty'));
    let warranties = cart.items.filter((i) => i.format && i.format.includes('warranty'));

    let account = {
        info: user,
        org,
        addresses: { data: addresses }
    };

    let shippingAddress = addresses.find((adr) => adr.primary_shipping) || {
        street: [],
        to: ''
    };

    let billingAddress = addresses.find((adr) => adr.primary_billing) || {
        street: [],
        to: ''
    };

    let unitCount = cartUtils.getUnitCount(cart.items);
    let ilsEnabled = org.ils_enabled && user.ils_enabled;

    return (
        <div className="checkout">
            <h2 className="l-text-center">
                {cartUtils.getName(cart, sopSub)} - Checkout (
                <Link to={`/customer/${params.user_id}/carts/${params.cart_id}`}>
                    <small>
                        {unitCount} {unitCount > 1 ? 'units' : 'unit'}
                    </small>
                </Link>
                )
            </h2>

            <hr className="spaced-double" />

            <div className="row">
                <aside className="col-xs-12 col-md-5 block-content no-print">
                    <div className="spaced-double">
                        <h5 className="text-orange">Billing Address</h5>
                        <AddressSelector
                            ref={_billing_selector}
                            step="billing_address"
                            account={account}
                            address={billingAddress}
                            showName={false}
                        />
                    </div>

                    <div className="spaced-double">
                        <h5 className="text-orange">Shipping Address</h5>
                        <Input label="Attn:" ref={_shipping_attn} id="attn" value={shippingAddress.attn} />
                        {/* <p>Select a shipping address from your address book or enter a new address.</p> */}
                        <AddressSelector
                            ref={_shipping_selector}
                            step="shipping_address"
                            account={account}
                            address={shippingAddress}
                            showNameFields={false}
                        />
                    </div>

                    <div className="spaced-double">
                        <h5 className="text-orange">Payment Method</h5>

                        {org.currency !== 'CAD' && !cart.sop_id && (
                            <div className="radio-inline">
                                <label htmlFor="cc">
                                    <input
                                        type="radio"
                                        id="cc"
                                        name="payment"
                                        value="cc"
                                        onChange={handlePaymentSelect}
                                        checked={paymentType === 'cc'}
                                    />{' '}
                                    Credit Card
                                </label>
                            </div>
                        )}
                        <div className="radio-inline">
                            <label htmlFor="invoice">
                                <input
                                    type="radio"
                                    id="invoice"
                                    name="payment"
                                    value="invoice"
                                    onChange={handlePaymentSelect}
                                    checked={paymentType === 'invoice'}
                                />{' '}
                                Invoice Me
                            </label>
                        </div>
                        <hr />

                        {paymentType === 'cc' && <PaymentForm ref={payment_form} />}

                        <PromoEntry
                            promos={cart.promos_applied}
                            userId={user._id}
                            cartId={cart._id}
                            onPromoUpdate={handleApplyPromo}
                        />

                        <Input ref={_po_number} label="Purchase Order Number" type="text" id="po_number" />
                    </div>
                    <div className="checkout-comments">
                        <TextArea
                            ref={order_notes}
                            label="Order Notes (optional)"
                            name="orderComment"
                            id="orderComment"
                            rows="3"
                            cols="60"
                        />
                    </div>

                    {canDelete && (
                        <Checkbox
                            id="remove_check"
                            label="Remove cart after submit"
                            onChange={handleRemoveCheck}
                            checked={shouldRemoveCart || false}
                        />
                    )}

                    <div className="spaced-single">
                        <button
                            type="button"
                            className="button button-primary"
                            onClick={handleSubmit}
                            disabled={inProcess}
                        >
                            Submit Order
                        </button>
                        <strong>
                            Order Total:{' '}
                            <FormattedNumber
                                value={formatCurrency(cartUtils.getGrandTotal(cart.items, org.price_contracts, true))}
                                currency={org.currency}
                            />
                        </strong>
                    </div>
                </aside>
                <section className="col-xs-12 col-md-7">
                    <ProductTable
                        className="no-margin"
                        account={account}
                        products={cart.items}
                        ilsEnabled={ilsEnabled}
                        cartId={cart._id}
                        priceContracts={org.price_contracts}
                        currency={org.currency}
                        territory={billingAddress && billingAddress.country}
                        isCart={true}
                        isReadOnly={true}
                        showListPrice={false}
                        showImages={false}
                        showAllocations={false}
                        showAudience={false}
                        showTotal={true}
                        showCheckbox={false}
                        showAllocHeader={false}
                        showAllocTotal={false}
                        meta={['isbn']}
                        condenced
                    />
                    <div className="row end-md">
                        <div className="col-xs-12 col-md-8">
                            <Totals
                                sub_total={formatCurrency(cartUtils.getSubtotal(onlyProducts, org.currency))}
                                shipping={formatCurrency(cartUtils.getShipping(cart.items))}
                                grand_total={formatCurrency(
                                    cartUtils.getGrandTotal(cart.items, org.price_contracts, true)
                                )}
                                discount={formatCurrency(cartUtils.getDiscount(onlyProducts, org.price_contracts))}
                                warranties={formatCurrency(cartUtils.getSubtotal(warranties, org.currency))}
                                unit_count={cartUtils.getUnitCount(cart.items)}
                                currency={org.currency}
                            />
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};
export default Checkout;
